<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" persistent scrollable max-width="550px")
    yz-card
      yz-dialog-normal-header(:title="$t('download_item', { item: itemName })" @close="onClose")

      yz-dialog-normal-content.pa-6
        // Loading state
        template(v-if="isLoading")
          div(style="width: 450px; margin: auto;")
            yz-skeleton-loader(type="article")

        // Error state
        template(v-else-if="isError")
          div(style="width: 450px; margin: auto;")
            yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('error_message.unable_to_load_data')" :description="errorMessage" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('retry')" @action-click="fetchTemplates")

        // Main content
        template(v-else)
          div(style="width: 450px; margin: auto;")
            // Description
            .yz-medium-body-md.text-neutral-700 {{ $t('download_item_description', { item: itemName }) }}

            // Template selection section
            .mt-6
              .yz-medium-body-sm.text-neutral-500 {{ $t('item_templates', { item: $options.filters.capitalize(itemName) }) }}
              v-radio-group.my-2(v-model="selectedDocument" mandatory hide-details)
                template(v-for="document in documents")
                  v-radio.mb-4(:key="document.id" :value="document" color="neutral700")
                    template(v-slot:label)
                      .neutral700--text.ml-2
                        .yz-medium-body-md {{ document.name }}
                        .yz-regular-body-sm.text-neutral-600 {{ document.description }}

      yz-dialog-normal-actions
        yz-btn(size="lg" text :label="$t('cancel')" @click="onClose")
        yz-btn(size="lg" :label="$t('confirm')" :loading="isDownloading" :disabled="isLoading || isError" @click="onConfirm")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    claim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isError: false,
      isDownloading: false,
      errorMessage: '',
      documents: [],
      selectedDocument: null
    }
  },
  mounted() {
    this.fetchTemplates()
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    itemName() {
      return this.$t('invoice').toLowerCase()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchInvoiceDetails() {
      const invoiceFetchPromises = this.claim.invoices.map(async (invoiceUid) => {
        try {
          const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, {
            params: { uids: invoiceUid }
          })
          if (data.results && data.results.length > 0) {
            return {
              ...data.results[0],
              pid: data.results[0].lead_pid
            }
          }
          return null
        } catch (error) {
          console.error(`Error fetching invoice ${invoiceUid}:`, error)
          return null
        }
      })

      return (await Promise.all(invoiceFetchPromises)).filter(invoice => invoice !== null)
    },
    async fetchTemplates() {
      this.isLoading = true
      this.isError = false
      this.errorMessage = ''

      try {
        const params = {
          company: this.company.id,
          status: 'active',
          categories: 'invoice'
        }
        const { data } = await this.$axios.get('/document_templates', { params })

        if (data.count === 0) {
          // No templates found - redirect to template creation
          this.isError = true
          this.errorMessage = this.$t('error_message.create_item_first', { item: this.$t('invoice').toLowerCase() })
          await this.showMessage(['error', this.errorMessage])
          await this.$router.push('/admin/clinic/settings/document/templates')
          this.onClose()
          return
        }

        this.documents = data.results

        // Auto-select if only one template
        if (data.count === 1) {
          this.selectedDocument = data.results[0]
          // Auto-download immediately
          await this.onConfirm()
        } else {
          // Multiple templates - show selection UI
          this.selectedDocument = data.results[0] // Pre-select first one
        }
      } catch (error) {
        console.error('Error fetching templates:', error)
        this.isError = true
        this.errorMessage = this.$t('error_message.unable_to_complete_request')
        await this.showMessage(['error', this.errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    onClose() {
      this.$emit('input', false)
      this.$emit('close')
    },
    async onConfirm() {
      if (!this.selectedDocument) {
        await this.showMessage(['warning', this.$t('error_message.please_select_template')])
        return
      }

      if (!this.claim || !this.claim.invoices || this.claim.invoices.length === 0) {
        await this.showMessage(['warning', 'No invoices found for this claim'])
        return
      }

      this.isDownloading = true

      try {
        // Fetch full invoice details for all invoices in the claim
        const invoiceDetails = await this.fetchInvoiceDetails()

        if (invoiceDetails.length === 0) {
          await this.showMessage(['error', 'Unable to fetch invoice details'])
          this.isDownloading = false
          return
        }

        if (invoiceDetails.length === 1) {
          // Single invoice: Use OTK + window.open (original behavior)
          const { data: otkData } = await this.$axios.post(`users/${this.user.id}/request_otk`)
          const key = otkData.key

          // Determine base URL
          let baseUrl = process.env.VUE_APP_BASE_URL
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            baseUrl = 'https://api-test.yezza.io'
          } else if (process.env.VUE_APP_ENV === 'prod') {
            baseUrl = 'https://export.yezza.io'
          }

          const invoice = invoiceDetails[0]
          const url = `${baseUrl}/invoices/${invoice.pid}/preview_document?document_template=${this.selectedDocument.id}&key=${key}`
          window.open(url, '_blank')
        } else {
          // Multiple invoices: Use bulk download endpoint without OTK
          const leadPids = invoiceDetails.map(invoice => invoice.pid).join(',')
          const params = {
            document_template: this.selectedDocument.id,
            ids: leadPids
          }

          // Download as blob to handle zip file
          const response = await this.$axios.get('/invoices/download', {
            params,
            responseType: 'blob'
          })

          // Extract filename from content-disposition header
          let filename = `invoices_${Date.now()}.zip`
          const contentDisposition = response.headers['content-disposition']
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/"/g, '')
            }
          }

          // Create blob and trigger download
          const blob = new Blob([response.data])
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          link.click()
          window.URL.revokeObjectURL(link.href)
        }

        await this.showMessage(['success', this.$t('panel.download_started')])
        this.onClose()
      } catch (error) {
        console.error('Error downloading invoices:', error)
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDownloading = false
      }
    }
  }
}
</script>
